import { useEffect, useMemo, useState } from 'react';

export type ResourceScope = 'scoped' | 'all';

export type UserResourcesQueryState = {
  scope: ResourceScope;
  resourceFilter: string;
  platformFilters: string[];
  resourceTypeFilters: string[];
  statusFilters: string[];
  showCancelled: boolean;
  showDeleted: boolean;
  expandAll: boolean;
};

const defaultQueryState: UserResourcesQueryState = {
  scope: 'scoped',
  resourceFilter: '',
  platformFilters: [],
  resourceTypeFilters: [],
  statusFilters: [],
  showCancelled: false,
  showDeleted: false,
  expandAll: true
};

const parseBooleanParam = (value: string | null): boolean | null => {
  if (value == null) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === '1' || normalized === 'true') {
    return true;
  }
  if (normalized === '0' || normalized === 'false') {
    return false;
  }

  return null;
};

const parseScopeParam = (value: string | null): ResourceScope | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'all') {
    return 'all';
  }
  if (normalized === 'scoped' || normalized === 'mine' || normalized === 'team') {
    return 'scoped';
  }

  return null;
};

const parseCsvParam = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map(item => item.trim().toLowerCase())
    .filter(Boolean);
};

const unique = (values: string[]): string[] => Array.from(new Set(values));

const parsePlatformParams = (params: URLSearchParams, options: string[]): string[] => {
  const fromCsv = parseCsvParam(params.get('platform'));
  const legacySingle = params.get('platform');
  const combined = unique([...fromCsv, ...(legacySingle ? [legacySingle.toLowerCase()] : [])]);
  return combined.filter(platform => options.includes(platform));
};

const parseResourceTypeParams = (params: URLSearchParams): string[] => {
  const fromCsv = parseCsvParam(params.get('resourceType'));
  const legacySingle = params.get('resourceType');
  return unique([...fromCsv, ...(legacySingle ? [legacySingle.trim().toLowerCase()] : [])]).filter(Boolean);
};

const parseStatusParams = (params: URLSearchParams): string[] => {
  return unique(parseCsvParam(params.get('status')));
};

const parseQueryStateFromUrl = (platformOptions: string[]): UserResourcesQueryState => {
  const params = new URLSearchParams(window.location.search);

  // Backward compatibility with older URL schema where all=1 toggled the global scope.
  const legacyAllParam = parseBooleanParam(params.get('all'));
  const explicitScope = parseScopeParam(params.get('scope'));
  const resolvedScope = explicitScope ?? (legacyAllParam ? 'all' : 'scoped');

  return {
    scope: resolvedScope,
    resourceFilter: params.get('q') ?? defaultQueryState.resourceFilter,
    platformFilters: parsePlatformParams(params, platformOptions),
    resourceTypeFilters: parseResourceTypeParams(params),
    statusFilters: parseStatusParams(params),
    showCancelled: parseBooleanParam(params.get('cancelled')) ?? defaultQueryState.showCancelled,
    showDeleted: parseBooleanParam(params.get('deleted')) ?? defaultQueryState.showDeleted,
    expandAll: parseBooleanParam(params.get('expand')) ?? defaultQueryState.expandAll
  };
};

const buildQueryString = (queryState: UserResourcesQueryState): string => {
  const params = new URLSearchParams();

  const trimmedFilter = queryState.resourceFilter.trim();
  if (trimmedFilter) {
    params.set('q', trimmedFilter);
  }

  if (queryState.platformFilters.length > 0) {
    params.set('platform', queryState.platformFilters.join(','));
  }

  if (queryState.resourceTypeFilters.length > 0) {
    params.set('resourceType', queryState.resourceTypeFilters.join(','));
  }

  if (queryState.statusFilters.length > 0) {
    params.set('status', queryState.statusFilters.join(','));
  }

  if (queryState.scope !== 'scoped') {
    params.set('scope', queryState.scope);
  }

  if (queryState.showCancelled) {
    params.set('cancelled', '1');
  }

  if (queryState.showDeleted) {
    params.set('deleted', '1');
  }

  if (!queryState.expandAll) {
    params.set('expand', '0');
  }

  const query = params.toString();
  return query ? `?${query}` : '';
};

export function useUserResourcesQueryState(platformOptions: string[]) {
  const [queryState, setQueryState] = useState<UserResourcesQueryState>(() => parseQueryStateFromUrl(platformOptions));

  useEffect(() => {
    const nextSearch = buildQueryString(queryState);
    const nextUrl = `${window.location.pathname}${nextSearch}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [queryState]);

  const actions = useMemo(() => ({
    setScope: (scope: ResourceScope) => setQueryState(prev => ({ ...prev, scope })),
    setResourceFilter: (resourceFilter: string) => setQueryState(prev => ({ ...prev, resourceFilter })),
    togglePlatformFilter: (platformFilter: string) => setQueryState(prev => {
      const normalized = platformFilter.toLowerCase().trim();
      if (!normalized) {
        return prev;
      }

      const exists = prev.platformFilters.includes(normalized);
      return {
        ...prev,
        platformFilters: exists
          ? prev.platformFilters.filter(value => value !== normalized)
          : [...prev.platformFilters, normalized]
      };
    }),
    toggleResourceTypeFilter: (resourceTypeFilter: string) => setQueryState(prev => {
      const normalized = resourceTypeFilter.toLowerCase().trim();
      if (!normalized) {
        return prev;
      }

      const exists = prev.resourceTypeFilters.includes(normalized);
      return {
        ...prev,
        resourceTypeFilters: exists
          ? prev.resourceTypeFilters.filter(value => value !== normalized)
          : [...prev.resourceTypeFilters, normalized]
      };
    }),
    toggleStatusFilter: (statusFilter: string) => setQueryState(prev => {
      const normalized = statusFilter.toLowerCase().trim();
      if (!normalized) {
        return prev;
      }

      const exists = prev.statusFilters.includes(normalized);
      return {
        ...prev,
        statusFilters: exists
          ? prev.statusFilters.filter(value => value !== normalized)
          : [...prev.statusFilters, normalized]
      };
    }),
    setShowCancelled: (showCancelled: boolean) => setQueryState(prev => ({ ...prev, showCancelled })),
    setShowDeleted: (showDeleted: boolean) => setQueryState(prev => ({ ...prev, showDeleted })),
    setExpandAll: (expandAll: boolean) => setQueryState(prev => ({ ...prev, expandAll })),
    clearFacetFilters: () => setQueryState(prev => ({ ...prev, platformFilters: [], resourceTypeFilters: [], statusFilters: [] })),
    removeFacetValue: (facet: 'platform' | 'resourceType' | 'status', value: string) => setQueryState(prev => {
      if (facet === 'platform') {
        return { ...prev, platformFilters: prev.platformFilters.filter(item => item !== value) };
      }
      if (facet === 'resourceType') {
        return { ...prev, resourceTypeFilters: prev.resourceTypeFilters.filter(item => item !== value) };
      }
      return { ...prev, statusFilters: prev.statusFilters.filter(item => item !== value) };
    }),
    resetAllFilters: () => setQueryState(prev => ({
      ...prev,
      resourceFilter: '',
      platformFilters: [],
      resourceTypeFilters: [],
      statusFilters: [],
      showCancelled: false,
      showDeleted: false
    }))
  }), []);

  return {
    queryState,
    ...actions
  };
}
